import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { AlertRule } from "../types/alertRule";

const COLLECTION_NAME = "alertRules";

/**
 * Service for managing alert rules (admin-only operations)
 */
export class AlertRuleService {
  /**
   * Fetch all alert rules
   */
  static async fetchAlertRules(): Promise<AlertRule[]> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      return snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as AlertRule;
      });
    } catch (error) {
      console.error("Error fetching alert rules:", error);
      throw new Error("Failed to fetch alert rules");
    }
  }

  /**
   * Create a new alert rule. Doc ID is the alertId, so alertId must be unique.
   */
  static async createAlertRule(
    ruleData: Omit<AlertRule, "id" | "createdAt" | "updatedAt">
  ): Promise<void> {
    const alertId = ruleData.alertId?.trim();
    if (!alertId) {
      throw new Error("Alert ID is required");
    }
    if (!ruleData.alertName?.trim()) {
      throw new Error("Alert name is required");
    }
    if (!ruleData.condition?.trim()) {
      throw new Error("Condition is required");
    }

    try {
      const docRef = doc(db, COLLECTION_NAME, alertId);
      const existing = await getDoc(docRef);
      if (existing.exists()) {
        throw new Error(`An alert rule with ID "${alertId}" already exists`);
      }

      await setDoc(docRef, {
        alertId,
        condition: ruleData.condition.trim(),
        alertTier: ruleData.alertTier,
        primaryTriggerDriver: ruleData.primaryTriggerDriver?.trim() || "",
        secondaryTriggerDriver: ruleData.secondaryTriggerDriver?.trim() || "",
        alertName: ruleData.alertName.trim(),
        athleteInsight: ruleData.athleteInsight?.trim() || "",
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error("Error creating alert rule:", error);
      throw error instanceof Error
        ? error
        : new Error("Failed to create alert rule");
    }
  }

  /**
   * Update an existing alert rule
   */
  static async updateAlertRule(
    ruleId: string,
    ruleData: Partial<Omit<AlertRule, "id" | "alertId" | "createdAt">>
  ): Promise<void> {
    if (!ruleId) {
      throw new Error("Alert rule ID is required");
    }

    try {
      const dataToUpdate: {
        updatedAt: Timestamp;
        condition?: string;
        alertTier?: AlertRule["alertTier"];
        primaryTriggerDriver?: string;
        secondaryTriggerDriver?: string;
        alertName?: string;
        athleteInsight?: string;
      } = {
        updatedAt: Timestamp.now(),
      };

      if (ruleData.condition !== undefined) {
        dataToUpdate.condition = ruleData.condition.trim();
      }
      if (ruleData.alertTier !== undefined) {
        dataToUpdate.alertTier = ruleData.alertTier;
      }
      if (ruleData.primaryTriggerDriver !== undefined) {
        dataToUpdate.primaryTriggerDriver = ruleData.primaryTriggerDriver.trim();
      }
      if (ruleData.secondaryTriggerDriver !== undefined) {
        dataToUpdate.secondaryTriggerDriver =
          ruleData.secondaryTriggerDriver.trim();
      }
      if (ruleData.alertName !== undefined) {
        dataToUpdate.alertName = ruleData.alertName.trim();
      }
      if (ruleData.athleteInsight !== undefined) {
        dataToUpdate.athleteInsight = ruleData.athleteInsight.trim();
      }

      await updateDoc(doc(db, COLLECTION_NAME, ruleId), dataToUpdate);
    } catch (error) {
      console.error("Error updating alert rule:", error);
      throw new Error("Failed to update alert rule");
    }
  }

  /**
   * Delete an alert rule
   */
  static async deleteAlertRule(ruleId: string): Promise<void> {
    if (!ruleId) {
      throw new Error("Alert rule ID is required");
    }

    try {
      await deleteDoc(doc(db, COLLECTION_NAME, ruleId));
    } catch (error) {
      console.error("Error deleting alert rule:", error);
      throw new Error("Failed to delete alert rule");
    }
  }
}
